/**
 * useFeature Hook - React hook for checking subscription features
 * SAFE: Does not make API calls until user is authenticated
 */

import {
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  useCallback,
  ReactNode,
} from 'react';
import { useAppSelector } from '../redux/stateHooks';
import { tokenManager } from '../utility/tokenManager';
import { TOKEN_TYPES } from '../constant/auth.constant';

const SUBSCRIPTION_STORAGE_KEY = 'classveew_subscription';

export const FEATURES = {
  CAN_MANAGE_STUDENTS: 'can_manage_students',
  CAN_MANAGE_TEACHERS: 'can_manage_teachers',
  CAN_MANAGE_ROLES: 'can_manage_roles',
  HAS_STUDENT_ATTENDANCE: 'has_student_attendance',
  STUDENT_ATTENDANCE_CHECKIN: 'student_attendance_checkin',
  HAS_TEACHER_ATTENDANCE: 'has_teacher_attendance',
  ANNOUNCEMENTS: 'announcements',
  EVENT_SURVEY: 'event_survey',
  PAID_EVENTS: 'paid_events',
  AWARD_ISSUE_LEVEL: 'award_issue_level',
  LIBRARY_MANAGEMENT: 'library_management',
  LIBRARY_BOOK_LIMIT: 'library_book_limit',
  LIBRARY_CIRCULATION: 'library_circulation',
  EXAM_MANAGEMENT: 'exam_management',
  CAN_MANAGE_ACADEMICS: 'can_manage_academics',
  CAN_MANAGE_TIMETABLE: 'can_manage_timetable',
  FEE_MANAGEMENT: 'fee_management',
  ONLINE_PAYMENT: 'online_payment',
  ACADEMIC_YEAR: 'academic_year',
  OPERATIONAL_TIME: 'operational_time',
} as const;

export type FeatureCode = (typeof FEATURES)[keyof typeof FEATURES];

interface SubscriptionContextType {
  subscription: any | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  hasFeature: (featureCode: string) => boolean;
  getFeatureValue: (featureCode: string) => string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

const getCachedSubscription = (): any | null => {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        // Invalidate stale cache that was stored without features array
        if (parsed.data && !Array.isArray(parsed.data.features)) {
          return null;
        }
        return parsed.data;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const setCachedSubscription = (subscription: any): void => {
  try {
    localStorage.setItem(
      SUBSCRIPTION_STORAGE_KEY,
      JSON.stringify({
        data: subscription,
        timestamp: Date.now(),
      }),
    );
  } catch (e) {
    // ignore
  }
};

export const clearSubscriptionCache = (): void => {
  try {
    localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscription, setSubscription] = useState<any | null>(getCachedSubscription());
  // Starts true whenever we haven't resolved a subscription yet, so consumers
  // gating on feature values (e.g. paid-event dropdown option) don't render
  // a false-negative before privileges/subscription have loaded (CVA-4217).
  const [loading, setLoading] = useState(() => !getCachedSubscription());

  // Get auth state from Redux - this is safe because Redux is already loaded
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated);

  const refreshSubscription = useCallback(async () => {
    // Only fetch if user is logged in.
    // isAuthenticated reflects Redux's persisted (localStorage) auth slice,
    // which can outlive the actual tokens (sessionStorage, tab-scoped) after
    // a tab close/browser restart. Without this second check we'd fire a
    // request that's guaranteed to 401 and force a hard logout redirect.
    if (!isAuthenticated || !tokenManager.hasValidToken(TOKEN_TYPES.REFRESH_TOKEN)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Dynamic import to avoid loading service before auth is ready.
      // `/me/subscription` is JWT-only and returns the plan features alongside
      // the subscription, so EVERY role can resolve feature locks — this must
      // NEVER be gated on BILLING_MANAGEMENT (CVA-4743): that privilege is
      // admin-only, so gating the fetch on it meant Teachers and Parents never
      // loaded feature data at all, and every SubscriptionFeatureGuard silently
      // failed open for them (its `!subscription` branch renders through).
      const { fetchMySubscriptionWithFeatures } = await import('../services/subscription.service');
      const { data } = await fetchMySubscriptionWithFeatures();

      if (!data.subscription) {
        // No subscription row yet (ETL setup incomplete) — nothing to gate on.
        setSubscription(null);
        clearSubscriptionCache();
        return;
      }

      // Attach flat features array directly onto subscription object
      const subWithFeatures = {
        ...data.subscription,
        features: data.features,
      };
      setSubscription(subWithFeatures);
      setCachedSubscription(subWithFeatures);
    } catch (err: any) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Clear subscription when logged out, OR when Redux still thinks we're
    // authenticated but the actual tokens are gone (stale localStorage-persisted
    // auth slice outliving the tab-scoped sessionStorage tokens).
    if (!isAuthenticated || !tokenManager.hasValidToken(TOKEN_TYPES.REFRESH_TOKEN)) {
      setSubscription(null);
      clearSubscriptionCache();
      setLoading(false);
      return;
    }

    // CVA-4743: the fetch only needs a valid auth token (checked above), not
    // any particular privilege — so unlike the old BILLING_MANAGEMENT-gated
    // version, there's no need to wait for a `privilegesUpdated` event here.
    if (!subscription) {
      refreshSubscription();
    }
    // Only depend on isAuthenticated - subscription check happens inside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const hasFeature = useCallback(
    (featureCode: string): boolean => {
      if (!subscription) return true; // Not loaded — don't block (let backend handle)
      // Pure trial (no plan assigned) → full access to everything
      // Matches backend: subscription.is_trial && !subscription.plan_id → true
      const isTrial = (subscription.is_trial || subscription.isTrial) && !subscription.plan_id;
      if (isTrial) return true;
      // Expired or cancelled subscription — block all features except dashboard and billing
      const subscriptionStatus = (subscription.status || '').toLowerCase();
      if (subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled') {
        return false;
      }
      // Use flat features array (fetched via /me/features and attached by refreshSubscription)
      const features: any[] = subscription.features || subscription.plan?.plan_features || [];
      // No feature data at all — conservative: allow through (backend guards)
      if (!features.length) return true;
      const feature = features.find(
        (f: any) => f.feature_code === featureCode || f.code === featureCode,
      );
      // Feature not in plan → plan does NOT include this module → BLOCK
      if (!feature) return false;
      const value = feature.value;
      return Boolean(value) && value !== 'false' && value !== '0' && value !== 'none';
    },
    [subscription],
  );

  const getFeatureValue = useCallback(
    (featureCode: string): string | null => {
      if (!subscription) return null;
      const features: any[] = subscription.features || subscription.plan?.plan_features || [];
      const feature = features.find(
        (f: any) => f.feature_code === featureCode || f.code === featureCode,
      );
      return feature?.value || null;
    },
    [subscription],
  );

  return (
    <SubscriptionContext.Provider
      value={{ subscription, loading, refreshSubscription, hasFeature, getFeatureValue }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

const useFeature = (featureCode: string) => {
  const { subscription, loading, hasFeature, getFeatureValue } = useSubscription();
  const hasAccess = hasFeature(featureCode);
  const isTrial = subscription?.is_trial || subscription?.isTrial || false;
  const value = getFeatureValue(featureCode);
  return { hasAccess, value, loading, isTrial };
};

export const useFeatures = (
  featureCodes?: (FeatureCode | string)[],
): {
  features: Record<string, any>;
  loading: boolean;
} => {
  const { subscription, loading } = useSubscription();

  const features = useMemo(() => {
    const output: Record<string, any> = {};

    const allFeatures: any[] = subscription?.features || subscription?.plan?.plan_features || [];

    if (!subscription || !allFeatures.length) {
      return output;
    }

    // Pure trial (no plan assigned) → full access, matches BE logic
    const isTrial = (subscription.is_trial || subscription.isTrial) && !subscription.plan_id;
    if (isTrial) {
      for (const pf of allFeatures) {
        output[pf.feature_code] = pf.value === 'false' ? false : pf.value || true;
      }
      return output;
    }

    if (featureCodes && featureCodes.length > 0) {
      for (const code of featureCodes) {
        const planFeature = allFeatures.find(
          (f: any) => f.feature_code === code || f.code === code,
        );
        if (planFeature) {
          const value = planFeature.value;
          output[code] = value === 'false' ? false : value === 'true' ? true : value;
        } else {
          output[code] = false; // Feature not in plan → BLOCK (matches hasFeature() behaviour)
        }
      }
    } else {
      for (const pf of allFeatures) {
        const value = pf.value;
        output[pf.feature_code] = value === 'false' ? false : value === 'true' ? true : value;
      }
    }

    return output;
  }, [subscription, featureCodes]);

  return { features, loading };
};

export default useFeature;
